import { useState, useEffect, useCallback } from 'react'
import { holidayService, type Holiday, type DayInfo } from '../services/holidayService'
import { logger } from '@/utils/logger'

interface VolunteerSlot {
  id: string
  date: string
  [key: string]: any
}

interface UseDayInfoReturn {
  getDayInfo: (dateStr: string) => DayInfo | null
  getHolidayInfo: (dateStr: string) => any
  isHoliday: (dateStr: string) => boolean
  isCampDay: (dateStr: string) => boolean
  isShabbat: (dateStr: string) => boolean
  isLoading: boolean
}

function normalizeDateFormat(dateStr: string): string {
  if (dateStr.includes('.')) {
    const [day, month, year] = dateStr.split('.')
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  }
  return dateStr
}

export function useDayInfo(slots: VolunteerSlot[]): UseDayInfoReturn {
  const [dayInfoCache, setDayInfoCache] = useState<Map<string, DayInfo>>(new Map())
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (slots.length === 0) return

    const loadHolidaysAndCamps = async () => {
      setIsLoading(true)
      try {
        logger.info('Loading holidays and camps cache', { component: 'useDayInfo' })
        await holidayService.getHolidays()

        const dayInfoCacheNew = new Map<string, DayInfo>()
        for (const slot of slots) {
          const dayInfo = await holidayService.getDayInfo(slot.date)
          if (dayInfo) {
            dayInfoCacheNew.set(normalizeDateFormat(slot.date), dayInfo)
          }
        }
        setDayInfoCache(dayInfoCacheNew)
      } catch (error) {
        logger.error('Failed to load holidays and camps cache', {
          component: 'useDayInfo',
          data: error
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadHolidaysAndCamps()
  }, [slots])

  const getDayInfo = useCallback((dateStr: string): DayInfo | null => {
    const normalizedDate = normalizeDateFormat(dateStr)
    const cached = dayInfoCache.get(normalizedDate)
    if (cached) return cached
    return holidayService.getDayInfoFallback(dateStr)
  }, [dayInfoCache])

  const getHolidayInfo = useCallback((dateStr: string): any => {
    const dayInfo = getDayInfo(dateStr)
    return dayInfo?.holiday || null
  }, [getDayInfo])

  const isHoliday = useCallback((dateStr: string): boolean => {
    const dayInfo = getDayInfo(dateStr)
    const holidayInfo = dayInfo?.holiday
    return !!(holidayInfo && (holidayInfo.type === 'holiday' || holidayInfo.type === 'break'))
  }, [getDayInfo])

  const isCampDay = useCallback((dateStr: string): boolean => {
    const dayInfo = getDayInfo(dateStr)
    return !!dayInfo?.camp
  }, [getDayInfo])

  const isShabbat = useCallback((dateStr: string): boolean => {
    const normalizedDate = normalizeDateFormat(dateStr)
    const date = new Date(normalizedDate)
    return date.getDay() === 6
  }, [])

  return {
    getDayInfo,
    getHolidayInfo,
    isHoliday,
    isCampDay,
    isShabbat,
    isLoading
  }
}
